import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const WorkloadDistributionChart = () => {
  const [viewType, setViewType] = useState('capacity'); // capacity, skills, location
  const [timeframe, setTimeframe] = useState('week');

  const capacityData = [
    {
      member: "Sarah J.",
      current: 85,
      capacity: 100,
      scheduled: 92,
      efficiency: 96,
      available: 15
    },
    {
      member: "Mike R.",
      current: 78,
      capacity: 100,
      scheduled: 88,
      efficiency: 92,
      available: 22
    },
    {
      member: "Emily C.",
      current: 72,
      capacity: 100,
      scheduled: 80,
      efficiency: 89,
      available: 28
    },
    {
      member: "David T.",
      current: 95,
      capacity: 100,
      scheduled: 98,
      efficiency: 87,
      available: 5
    },
    {
      member: "Lisa P.",
      current: 65,
      capacity: 100,
      scheduled: 70,
      efficiency: 84,
      available: 35
    }
  ];

  const skillDistribution = [
    { skill: "Pest Control", count: 12, color: "#2D5A27" },
    { skill: "Equipment Maintenance", count: 8, color: "#8B4513" },
    { skill: "Data Analysis", count: 6, color: "#F4A460" },
    { skill: "Quality Control", count: 10, color: "#059669" },
    { skill: "Field Coordination", count: 4, color: "#D97706" }
  ];

  const locationWorkload = [
    {
      location: "Field A",
      tasks: 15,
      hours: 45,
      priority: "high",
      completion: 78
    },
    {
      location: "Field B", 
      tasks: 12,
      hours: 38,
      priority: "medium",
      completion: 85
    },
    {
      location: "Field C",
      tasks: 8,
      hours: 24,
      priority: "low",
      completion: 92
    },
    {
      location: "Field D",
      tasks: 18,
      hours: 52,
      priority: "high",
      completion: 65
    },
    {
      location: "Equipment Shed",
      tasks: 6,
      hours: 18,
      priority: "medium",
      completion: 100
    }
  ];

  const predictiveRecommendations = [
    {
      id: 1,
      type: "overload",
      member: "David Thompson",
      message: "Approaching capacity limit. Consider redistributing 2-3 tasks.",
      priority: "high",
      icon: "AlertTriangle"
    },
    {
      id: 2,
      type: "underutilized",
      member: "Lisa Park",
      message: "Available for additional tasks. Skill match: Data Entry, Field Assistance.",
      priority: "medium",
      icon: "TrendingUp"
    },
    {
      id: 3,
      type: "efficiency",
      member: "Sarah Johnson",
      message: "Consistently exceeding targets. Consider advanced task assignments.",
      priority: "low",
      icon: "Star"
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 agricultural-shadow">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-xs">
              <div 
                className="w-3 h-3 rounded" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">{entry?.dataKey}:</span>
              <span className="font-medium text-foreground">{entry?.value}%</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getRecommendationColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-error bg-error/10';
      case 'medium': return 'border-warning bg-warning/10';
      case 'low': return 'border-success bg-success/10';
      default: return 'border-border bg-muted/10';
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Chart */}
      <div className="bg-card rounded-lg border border-border agricultural-shadow">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Workload Distribution & Capacity Planning</h3>
            <div className="flex items-center space-x-2">
              <select
                value={viewType}
                onChange={(e) => setViewType(e?.target?.value)}
                className="px-3 py-1 border border-border rounded-md text-sm bg-background"
              >
                <option value="capacity">Capacity View</option>
                <option value="skills">Skills Distribution</option>
                <option value="location">Location Workload</option>
              </select>
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e?.target?.value)}
                className="px-3 py-1 border border-border rounded-md text-sm bg-background"
              >
                <option value="day">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-6">
          {viewType === 'capacity' && (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={capacityData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="member" 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="current" fill="var(--color-primary)" name="Current Load" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="scheduled" fill="var(--color-secondary)" name="Scheduled" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="available" fill="var(--color-success)" name="Available" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {viewType === 'skills' && (
            <div className="flex items-center justify-center">
              <div className="w-80 h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={skillDistribution}
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="count"
                      label={({ skill, count }) => `${skill}: ${count}`}
                    >
                      {skillDistribution?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {viewType === 'location' && (
            <div className="space-y-4">
              {locationWorkload?.map((location) => (
                <div key={location?.location} className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-col">
                      <h4 className="text-sm font-medium text-foreground">{location?.location}</h4>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <span>{location?.tasks} tasks</span>
                        <span>•</span>
                        <span>{location?.hours} hours</span>
                        <span>•</span>
                        <span className={getPriorityColor(location?.priority)}>
                          {location?.priority} priority
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-foreground">{location?.completion}%</p>
                      <p className="text-xs text-muted-foreground">Complete</p>
                    </div>
                    <div className="w-20 bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${location?.completion}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Predictive Recommendations */}
      <div className="bg-card rounded-lg border border-border agricultural-shadow">
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Brain" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Predictive Scheduling Recommendations</h3>
          </div>
        </div>
        
        <div className="p-6 space-y-3">
          {predictiveRecommendations?.map((rec) => (
            <div key={rec?.id} className={`p-4 rounded-lg border ${getRecommendationColor(rec?.priority)}`}>
              <div className="flex items-start space-x-3">
                <Icon name={rec?.icon} size={18} className={getPriorityColor(rec?.priority)} />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-medium text-foreground">{rec?.member}</h4>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(rec?.priority)} bg-current/10`}>
                      {rec?.priority}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{rec?.message}</p>
                </div>
                <button className="px-3 py-1 bg-primary text-primary-foreground rounded text-xs hover:bg-primary/90 transition-colors">
                  Apply
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkloadDistributionChart;